/**
 * TimeScrollColumn
 *
 * Single vertical scroll column with snap behavior for time selection.
 * Supports tappable keyboard input for direct value entry.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  NativeScrollEvent,
  NativeSyntheticEvent,
  Pressable,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';

import { BorderRadius, Spacing } from '@/constants/spacing';
import { getBodyFont } from '@/constants/typography';

const ITEM_HEIGHT = 44;
const VISIBLE_COUNT = 5;
const COLUMN_HEIGHT = ITEM_HEIGHT * VISIBLE_COUNT;
const CONTENT_PADDING = ITEM_HEIGHT * 2; // top/bottom padding for centering

type TimeScrollColumnProps = {
  values: number[];
  selectedValue: number;
  onValueChange: (value: number) => void;
  formatValue: (value: number) => string;
  accentColor: string;
  textColor: string;
  textMutedColor: string;
  cardBackgroundColor: string;
  label: 'hour' | 'minute';
  snapToNearest?: number;
};

export function TimeScrollColumn({
  values,
  selectedValue,
  onValueChange,
  formatValue,
  accentColor,
  textColor,
  textMutedColor,
  cardBackgroundColor,
  label,
  snapToNearest,
}: TimeScrollColumnProps) {
  const scrollViewRef = useRef<Animated.ScrollView>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const inputRef = useRef<TextInput>(null);
  const isUserScrolling = useRef(false);
  const fadeOpacity = useSharedValue(1);

  const selectedIndex = values.indexOf(selectedValue);

  // Scroll to selected value on mount and when value changes externally
  useEffect(() => {
    if (!isUserScrolling.current && selectedIndex >= 0) {
      const offset = selectedIndex * ITEM_HEIGHT;
      requestAnimationFrame(() => {
        scrollViewRef.current?.scrollTo({ y: offset, animated: false });
      });
    }
  }, [selectedIndex]);

  const handleMomentumScrollEnd = useCallback(
    (event: NativeSyntheticEvent<NativeScrollEvent>) => {
      isUserScrolling.current = false;
      const offsetY = event.nativeEvent.contentOffset.y;
      const index = Math.round(offsetY / ITEM_HEIGHT);
      const clampedIndex = Math.max(0, Math.min(index, values.length - 1));
      const newValue = values[clampedIndex];

      if (newValue !== selectedValue) {
        onValueChange(newValue);
      }
    },
    [values, selectedValue, onValueChange],
  );

  const handleScrollBeginDrag = useCallback(() => {
    isUserScrolling.current = true;
  }, []);

  const handleCenterTap = useCallback(() => {
    setEditText(formatValue(selectedValue));
    setIsEditing(true);
    fadeOpacity.value = withTiming(0.3, { duration: 150 });

    setTimeout(() => inputRef.current?.focus(), 50);
  }, [selectedValue, formatValue, fadeOpacity]);

  const commitEditValue = useCallback(
    (text: string) => {
      const parsed = parseInt(text, 10);
      if (isNaN(parsed)) {
        setIsEditing(false);
        fadeOpacity.value = withTiming(1, { duration: 150 });
        return;
      }

      let clamped = Math.max(values[0], Math.min(parsed, values[values.length - 1]));

      if (snapToNearest) {
        clamped = Math.round(clamped / snapToNearest) * snapToNearest;
        if (clamped > values[values.length - 1]) {
          clamped = values[values.length - 1];
        }
      }

      const targetIndex = values.indexOf(clamped);
      if (targetIndex >= 0) {
        scrollViewRef.current?.scrollTo({
          y: targetIndex * ITEM_HEIGHT,
          animated: true,
        });
        onValueChange(clamped);
      }

      setIsEditing(false);
      fadeOpacity.value = withTiming(1, { duration: 150 });
    },
    [values, snapToNearest, onValueChange, fadeOpacity],
  );

  const handleSubmitEditing = useCallback(() => {
    commitEditValue(editText);
  }, [editText, commitEditValue]);

  const handleBlur = useCallback(() => {
    commitEditValue(editText);
  }, [editText, commitEditValue]);

  const scrollFadeStyle = useAnimatedStyle(() => ({
    opacity: fadeOpacity.value,
  }));

  return (
    <View style={styles.container}>
      {/* Scroll area */}
      <Animated.View style={[styles.scrollWrapper, scrollFadeStyle]}>
        {/* Top fade overlay */}
        <View
          style={[styles.fadeOverlayTop, { backgroundColor: cardBackgroundColor }]}
          pointerEvents="none"
        />

        {/* Selected row highlight */}
        <View
          style={[
            styles.selectedHighlight,
            { backgroundColor: accentColor + '18', borderColor: accentColor + '40' },
          ]}
          pointerEvents="none"
        />

        <Animated.ScrollView
          ref={scrollViewRef}
          showsVerticalScrollIndicator={false}
          snapToInterval={ITEM_HEIGHT}
          decelerationRate="fast"
          contentContainerStyle={{ paddingVertical: CONTENT_PADDING }}
          onMomentumScrollEnd={handleMomentumScrollEnd}
          onScrollBeginDrag={handleScrollBeginDrag}
          style={styles.scrollView}
          nestedScrollEnabled
        >
          {values.map((value, index) => {
            const isSelected = index === selectedIndex;
            return (
              <Pressable
                key={value}
                style={styles.itemContainer}
                onPress={isSelected ? handleCenterTap : undefined}
              >
                <Text
                  style={[
                    styles.itemText,
                    {
                      fontFamily: getBodyFont(),
                      color: isSelected ? accentColor : textColor,
                      fontWeight: isSelected ? '700' : '400',
                      fontSize: isSelected ? 22 : 16,
                    },
                  ]}
                >
                  {formatValue(value)}
                </Text>
              </Pressable>
            );
          })}
        </Animated.ScrollView>

        {/* Bottom fade overlay */}
        <View
          style={[styles.fadeOverlayBottom, { backgroundColor: cardBackgroundColor }]}
          pointerEvents="none"
        />
      </Animated.View>

      {/* Keyboard input overlay */}
      {isEditing && (
        <View style={[styles.editOverlay, { backgroundColor: cardBackgroundColor }]}>
          <TextInput
            ref={inputRef}
            value={editText}
            onChangeText={setEditText}
            onSubmitEditing={handleSubmitEditing}
            onBlur={handleBlur}
            keyboardType="number-pad"
            maxLength={2}
            style={[
              styles.editInput,
              {
                color: accentColor,
                fontFamily: getBodyFont(),
                borderBottomColor: accentColor,
              },
            ]}
            selectTextOnFocus
          />
          <Text style={[styles.editLabel, { color: textMutedColor, fontFamily: getBodyFont() }]}>
            {label}
          </Text>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 72,
    height: COLUMN_HEIGHT,
    position: 'relative',
  },
  scrollWrapper: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  scrollView: {
    height: COLUMN_HEIGHT,
  },
  itemContainer: {
    height: ITEM_HEIGHT,
    justifyContent: 'center',
    alignItems: 'center',
  },
  itemText: {
    textAlign: 'center',
  },
  selectedHighlight: {
    position: 'absolute',
    top: CONTENT_PADDING,
    left: Spacing.xs,
    right: Spacing.xs,
    height: ITEM_HEIGHT,
    borderRadius: BorderRadius.sm,
    borderWidth: 1,
    zIndex: 1,
  },
  fadeOverlayTop: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: ITEM_HEIGHT * 1.5,
    zIndex: 2,
    opacity: 0.7,
  },
  fadeOverlayBottom: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: ITEM_HEIGHT * 1.5,
    zIndex: 2,
    opacity: 0.7,
  },
  editOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
  },
  editInput: {
    fontSize: 28,
    fontWeight: '700',
    textAlign: 'center',
    width: 56,
    borderBottomWidth: 2,
    paddingVertical: Spacing.xs,
  },
  editLabel: {
    fontSize: 11,
    marginTop: Spacing.xs,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
});
